// apps/office/src/pages/Attendance.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import pb from '../lib/pocketbaseClient';
import { Calendar, Users, UserCheck, UserX, Clock, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Attendance() {
  const { user, role } = useAuth();
  const [labours, setLabours] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterVendor, setFilterVendor] = useState('All');
  const [filterSite, setFilterSite] = useState('All');
  const [filterSkill, setFilterSkill] = useState('All');
  const [search, setSearch] = useState('');
  const [updating, setUpdating] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    fetchData();
    return () => { isMounted.current = false; };
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      const [laboursRes, vendorsRes, sitesRes] = await Promise.all([
        pb.collection('labour').getFullList({
          sort: 'name',
          expand: 'vendor,site',
          filter: 'status="active"',
        }),
        pb.collection('vendors').getFullList({ sort: 'name' }),
        pb.collection('accounts_projects').getFullList({ sort: 'name' }),
      ]);
      if (isMounted.current) {
        setLabours(laboursRes);
        setVendors(vendorsRes);
        setSites(sitesRes);
        await fetchAttendanceForDate(laboursRes, selectedDate);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load data');
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const fetchAttendanceForDate = async (laboursList, date) => {
    const map = {};
    for (const lab of laboursList) {
      try {
        const records = await pb.collection('labour_attendance').getFullList({
          filter: `labour="${lab.id}" && date="${date}"`,
        });
        map[lab.id] = records.length > 0 ? records[0] : null;
      } catch {
        map[lab.id] = null;
      }
    }
    setAttendanceMap(map);
  };

  // --- Check-in --------------------------------------------------------------
  const handleCheckIn = async (labourId) => {
    setUpdating(true);
    try {
      const now = new Date().toISOString();
      const data = {
        labour: labourId,
        date: selectedDate,
        check_in: now,
        status: 'present', // default present on check-in
      };
      const record = await pb.collection('labour_attendance').create(data);
      setAttendanceMap(prev => ({ ...prev, [labourId]: record }));
      toast.success('Checked in');
    } catch (err) {
      console.error(err);
      toast.error('Check-in failed');
    } finally {
      setUpdating(false);
    }
  };

  // --- Check-out --------------------------------------------------------------
  const handleCheckOut = async (labourId, attendanceId) => {
    setUpdating(true);
    try {
      const now = new Date();
      const nowISO = now.toISOString();
      const hour = now.getHours();
      const isBefore2PM = hour < 14;
      const status = isBefore2PM ? 'halfday' : 'present';
      
      const updated = await pb.collection('labour_attendance').update(attendanceId, {
        check_out: nowISO,
        status: status,
      });
      setAttendanceMap(prev => ({ ...prev, [labourId]: updated }));
      toast.success(`Checked out${isBefore2PM ? ' (before 2 PM ? half day)' : ''}`);
    } catch (err) {
      console.error(err);
      toast.error('Check-out failed');
    } finally {
      setUpdating(false);
    }
  };

  // --- Manual Status Update (works for any date) --------------------------
  const handleStatusChange = async (labourId, attendanceId, newStatus) => {
    setUpdating(true);
    try {
      let updated;
      if (attendanceId) {
        updated = await pb.collection('labour_attendance').update(attendanceId, { status: newStatus });
      } else {
        // Create a record with only status (no check-in/out)
        const data = {
          labour: labourId,
          date: selectedDate,
          status: newStatus,
        };
        updated = await pb.collection('labour_attendance').create(data);
      }
      setAttendanceMap(prev => ({ ...prev, [labourId]: updated }));
      toast.success(`Status set to ${newStatus}`);
    } catch (err) {
      console.error(err);
      toast.error('Status update failed');
    } finally {
      setUpdating(false);
    }
  };

  // --- Bulk Mark All Present ------------------------------------------------
  const markAllPresent = async () => {
    if (!window.confirm(`Mark all active labours as Present for ${selectedDate}?`)) return;
    setUpdating(true);
    try {
      for (const lab of labours) {
        const att = attendanceMap[lab.id];
        await handleStatusChange(lab.id, att?.id, 'present');
      }
      toast.success('All marked Present');
    } catch (err) {
      toast.error('Failed');
    } finally {
      setUpdating(false);
    }
  };

  // --- Helpers ---------------------------------------------------------------
  const formatTime = (dateString) => {
    if (!dateString) return '�';
    try {
      return new Date(dateString).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '�';
    }
  };

  const getStatusBadge = (status) => {
    if (!status || status === 'absent') {
      return <Badge variant="outline" className="text-red-600 border-red-300">Absent</Badge>;
    }
    if (status === 'present') {
      return <Badge className="bg-emerald-100 text-emerald-700 border-0">Present</Badge>;
    }
    if (status === 'halfday') {
      return <Badge className="bg-yellow-100 text-yellow-700 border-0">Half Day</Badge>;
    }
    return <Badge variant="outline">Unknown</Badge>;
  };

  // --- Filters --------------------------------------------------------------
  const filteredLabours = labours.filter(l => {
    const matchVendor = filterVendor === 'All' || l.vendor === filterVendor;
    const matchSite = filterSite === 'All' || l.site === filterSite;
    const matchSkill = filterSkill === 'All' || l.skill === filterSkill;
    const matchSearch = l.name?.toLowerCase().includes(search.toLowerCase()) ||
                        l.aadhar?.includes(search);
    return matchVendor && matchSite && matchSkill && matchSearch;
  });

  // --- Stats ----------------------------------------------------------------
  const totalActive = filteredLabours.length;
  const presentToday = filteredLabours.filter(l => {
    const att = attendanceMap[l.id];
    return att && att.status === 'present';
  }).length;
  const halfDayToday = filteredLabours.filter(l => {
    const att = attendanceMap[l.id];
    return att && att.status === 'halfday';
  }).length;
  const absentToday = filteredLabours.filter(l => {
    const att = attendanceMap[l.id];
    return !att || att.status === 'absent';
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading attendance...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-amber-500" />
            <span>Labour Attendance</span>
            <Badge variant="outline" className="ml-2">{totalActive}</Badge>
          </h1>
          <p className="text-sm text-slate-500">Check-in / Check-out with automatic half-day detection</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-1.5 shadow-sm">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border-0 focus:outline-none text-sm py-1"
            />
          </div>
          <Button
            variant="outline"
            onClick={markAllPresent}
            disabled={updating}
            className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Mark All Present
          </Button>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={updating}
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Active Labours</p>
                <p className="text-2xl font-bold text-slate-800">{totalActive}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">? Present</p>
                <p className="text-2xl font-bold text-emerald-600">{presentToday}</p>
              </div>
              <UserCheck className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">? Half Day</p>
                <p className="text-2xl font-bold text-yellow-600">{halfDayToday}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">? Absent</p>
                <p className="text-2xl font-bold text-red-600">{absentToday}</p>
              </div>
              <UserX className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="?? Search labour..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={filterVendor}
          onChange={(e) => setFilterVendor(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
        >
          <option value="All">All Vendors</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select
          value={filterSite}
          onChange={(e) => setFilterSite(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
        >
          <option value="All">All Sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select
          value={filterSkill}
          onChange={(e) => setFilterSkill(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
        >
          <option value="All">All Skills</option>
          <option value="skilled">Skilled</option>
          <option value="unskilled">Unskilled</option>
        </select>
        <Button variant="outline" onClick={() => { setSearch(''); setFilterVendor('All'); setFilterSite('All'); setFilterSkill('All'); }}>
          Clear
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Name</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Vendor</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Site</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Check-In</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Check-Out</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredLabours.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">No active labours found.</td></tr>
              ) : (
                filteredLabours.map((lab) => {
                  const att = attendanceMap[lab.id];
                  const vendor = vendors.find(v => v.id === lab.vendor);
                  const currentStatus = att?.status || 'absent';
                  const hasCheckIn = att && att.check_in;
                  const hasCheckOut = att && att.check_out;

                  return (
                    <tr key={lab.id} className="hover:bg-slate-50/50 transition">
                      <td className="px-4 py-3 font-medium flex items-center gap-2">
                        {lab.photo && (
                          <img
                            src={pb.files.getUrl(lab, lab.photo)}
                            alt={lab.name}
                            className="w-6 h-6 rounded-full object-cover border"
                          />
                        )}
                        {lab.name}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {vendor?.photo && (
                            <img
                              src={pb.files.getUrl(vendor, vendor.photo)}
                              alt={vendor.name}
                              className="w-5 h-5 rounded-full object-cover border"
                            />
                          )}
                          {vendor?.name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3">{lab.expand?.site?.name || 'N/A'}</td>
                      <td className="px-4 py-3 font-mono text-sm">
                        {hasCheckIn ? formatTime(att.check_in) : '�'}
                      </td>
                      <td className="px-4 py-3 font-mono text-sm">
                        {hasCheckOut ? formatTime(att.check_out) : '�'}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={currentStatus}
                          onChange={(e) => handleStatusChange(lab.id, att?.id, e.target.value)}
                          disabled={updating}
                          className="px-2 py-1 border rounded text-sm bg-white focus:ring-2 focus:ring-amber-400 w-full max-w-[120px]"
                        >
                          <option value="present">? Present</option>
                          <option value="halfday">? Half Day</option>
                          <option value="absent">? Absent</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {!hasCheckIn && (
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => handleCheckIn(lab.id)}
                              disabled={updating}
                            >
                              Check-In
                            </Button>
                          )}
                          {hasCheckIn && !hasCheckOut && (
                            <Button
                              size="sm"
                              className="bg-amber-600 hover:bg-amber-700 text-white"
                              onClick={() => handleCheckOut(lab.id, att.id)}
                              disabled={updating}
                            >
                              Check-Out
                            </Button>
                          )}
                          {hasCheckIn && hasCheckOut && (
                            <Badge variant="outline" className="text-emerald-600 border-emerald-300">? Done</Badge>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 text-xs text-slate-500 flex flex-wrap items-center gap-4">
        <span className="font-medium">?? Rules:</span>
        <span>� <span className="text-emerald-600 font-medium">Check-In</span> ? Present (auto)</span>
        <span>� <span className="text-amber-600 font-medium">Check-Out before 2 PM</span> ? Half Day (auto)</span>
        <span>� <span className="text-blue-600 font-medium">Status dropdown</span> � manual override for any date</span>
        <span className="ml-auto">?? Change date to mark attendance for past/future days</span>
      </div>
    </div>
  );
}